/**
 * Topic Review Modal Component
 *
 * A non-intrusive slide-in panel for collecting topic feedback.
 * Features:
 * - Initial thumbs up/neutral/thumbs down selection
 * - Expands to show 5-star rating and feedback questions after sentiment selection
 * - Auto-saves on every change with debounce
 * - Guaranteed save on page unload via Beacon API
 * - Ergonomic design that doesn't block navigation
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { topicReviewService } from '../../../services/topicReview.service';
import type {
    TopicReviewModalProps,
    TopicReviewCreateRequest,
    ReviewSentiment,
    TopicReviewResponse
} from '../../../types/topicReview.types';

const AUTOSAVE_DEBOUNCE_MS = 800;

export const TopicReviewModal: React.FC<TopicReviewModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    enrollmentId,
    courseId,
    topicId,
    segmentId,
    lessonId,
    moduleId,
    topicTitle,
    existingReview
}) => {
    // Form state
    const [sentiment, setSentiment] = useState<ReviewSentiment | null>(null);
    const [starRating, setStarRating] = useState<number | null>(null);
    const [likedMost, setLikedMost] = useState('');
    const [couldImprove, setCouldImprove] = useState('');
    const [wantMoreOf, setWantMoreOf] = useState('');
    const [additionalComments, setAdditionalComments] = useState('');

    // UI state
    const [isExpanded, setIsExpanded] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [hoveredStar, setHoveredStar] = useState<number | null>(null);

    // Refs for tracking changes and debounce
    const hasChangesRef = useRef(false);
    const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastSavedDataRef = useRef<string>('');

    // Reset form state when topicId changes or modal opens for a new topic
    useEffect(() => {
        if (isOpen) {
            // Reset all form state for new topic
            setSentiment(null);
            setStarRating(null);
            setLikedMost('');
            setCouldImprove('');
            setWantMoreOf('');
            setAdditionalComments('');
            setIsExpanded(false);
            setSaveStatus('idle');
            hasChangesRef.current = false;
            lastSavedDataRef.current = '';

            // If there's an existing review, populate form with it
            if (existingReview) {
                setSentiment(existingReview.sentiment);
                setStarRating(existingReview.star_rating);
                setLikedMost(existingReview.liked_most || '');
                setCouldImprove(existingReview.could_improve || '');
                setWantMoreOf(existingReview.want_more_of || '');
                setAdditionalComments(existingReview.additional_comments || '');
                setIsExpanded(true);
            }
        }
    }, [isOpen, topicId, existingReview]);

    // Build request object
    const buildRequest = useCallback((isPartial: boolean = true): TopicReviewCreateRequest | null => {
        if (!sentiment) return null;

        return {
            enrollment_id: enrollmentId,
            course_id: courseId,
            topic_id: topicId,
            segment_id: segmentId,
            lesson_id: lessonId,
            module_id: moduleId,
            sentiment,
            star_rating: starRating || undefined,
            liked_most: likedMost || undefined,
            could_improve: couldImprove || undefined,
            want_more_of: wantMoreOf || undefined,
            additional_comments: additionalComments || undefined,
            is_partial: isPartial
        };
    }, [sentiment, starRating, likedMost, couldImprove, wantMoreOf, additionalComments,
        enrollmentId, courseId, topicId, segmentId, lessonId, moduleId]);

    // Auto-save function
    const autoSave = useCallback(async () => {
        const request = buildRequest(true);
        if (!request) return;

        const dataString = JSON.stringify(request);
        if (dataString === lastSavedDataRef.current) return;

        setIsSaving(true);
        setSaveStatus('saving');

        try {
            const result = await topicReviewService.submitReview(request);
            if (result.success) {
                lastSavedDataRef.current = dataString;
                hasChangesRef.current = false;
                setSaveStatus('saved');
                setTimeout(() => setSaveStatus('idle'), 2000);
            } else {
                setSaveStatus('error');
            }
        } catch (error) {
            console.error('[TopicReviewModal] Auto-save error:', error);
            setSaveStatus('error');
        } finally {
            setIsSaving(false);
        }
    }, [buildRequest]);

    // Trigger auto-save with debounce
    const triggerAutoSave = useCallback(() => {
        hasChangesRef.current = true;

        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
        }

        autoSaveTimeoutRef.current = setTimeout(() => {
            autoSave();
        }, AUTOSAVE_DEBOUNCE_MS);
    }, [autoSave]);

    // Handle sentiment selection
    const handleSentimentSelect = (selected: ReviewSentiment) => {
        setSentiment(selected);
        setIsExpanded(true);
        triggerAutoSave();
    };

    // Handle star rating
    const handleStarClick = (rating: number) => {
        setStarRating(rating);
        triggerAutoSave();
    };

    // Handle text input changes
    const handleTextChange = (
        setter: React.Dispatch<React.SetStateAction<string>>,
        value: string
    ) => {
        setter(value);
        triggerAutoSave();
    };

    // Handle final submission
    const handleSubmit = async () => {
        const request = buildRequest(false);
        if (!request) return;

        setIsSaving(true);
        setSaveStatus('saving');

        try {
            const result = await topicReviewService.submitReview(request);
            if (result.success) {
                lastSavedDataRef.current = JSON.stringify(request);
                hasChangesRef.current = false;
                setSaveStatus('saved');
                onSubmit?.();
                setTimeout(() => onClose(), 500);
            } else {
                setSaveStatus('error');
            }
        } catch (error) {
            console.error('[TopicReviewModal] Submit error:', error);
            setSaveStatus('error');
        } finally {
            setIsSaving(false);
        }
    };

    // Handle close - save before closing
    const handleClose = async () => {
        if (hasChangesRef.current && sentiment) {
            await autoSave();
        }
        onClose();
    };

    // Save on page unload
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (hasChangesRef.current && sentiment) {
                const request = buildRequest(true);
                if (request) {
                    topicReviewService.sendBeacon(request);
                }
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden' && hasChangesRef.current && sentiment) {
                const request = buildRequest(true);
                if (request) {
                    topicReviewService.sendBeacon(request);
                }
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
        };
    }, [sentiment, buildRequest]);

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop - semi-transparent, clickable to close */}
            <div
                className="fixed inset-0 bg-black/20 z-40 transition-opacity"
                onClick={handleClose}
            />

            {/* Slide-in Panel */}
            <div className="fixed right-0 top-1/2 -translate-y-1/2 z-50 w-full max-w-md p-4 animate-slide-in-right">
                <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-white">Quick Feedback</h3>
                                {topicTitle && (
                                    <p className="text-indigo-200 text-sm mt-0.5 truncate max-w-[280px]">
                                        {topicTitle}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={handleClose}
                                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                            >
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-5">
                        {/* Sentiment Selection */}
                        <div className="mb-6">
                            <p className="text-gray-300 text-sm mb-3">How was this topic?</p>
                            <div className="flex justify-center gap-4">
                                {/* Thumbs Up */}
                                <button
                                    onClick={() => handleSentimentSelect('positive')}
                                    className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-all ${
                                        sentiment === 'positive'
                                            ? 'bg-green-600/30 border-2 border-green-500 scale-105'
                                            : 'bg-slate-700/50 border-2 border-transparent hover:bg-slate-700 hover:scale-105'
                                    }`}
                                >
                                    <span className="text-3xl">&#128077;</span>
                                    <span className={`text-xs font-medium ${
                                        sentiment === 'positive' ? 'text-green-400' : 'text-gray-400'
                                    }`}>Loved it</span>
                                </button>

                                {/* Neutral */}
                                <button
                                    onClick={() => handleSentimentSelect('neutral')}
                                    className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-all ${
                                        sentiment === 'neutral'
                                            ? 'bg-yellow-600/30 border-2 border-yellow-500 scale-105'
                                            : 'bg-slate-700/50 border-2 border-transparent hover:bg-slate-700 hover:scale-105'
                                    }`}
                                >
                                    <span className="text-3xl">&#128528;</span>
                                    <span className={`text-xs font-medium ${
                                        sentiment === 'neutral' ? 'text-yellow-400' : 'text-gray-400'
                                    }`}>It's okay</span>
                                </button>

                                {/* Thumbs Down */}
                                <button
                                    onClick={() => handleSentimentSelect('negative')}
                                    className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-all ${
                                        sentiment === 'negative'
                                            ? 'bg-red-600/30 border-2 border-red-500 scale-105'
                                            : 'bg-slate-700/50 border-2 border-transparent hover:bg-slate-700 hover:scale-105'
                                    }`}
                                >
                                    <span className="text-3xl">&#128078;</span>
                                    <span className={`text-xs font-medium ${
                                        sentiment === 'negative' ? 'text-red-400' : 'text-gray-400'
                                    }`}>Needs work</span>
                                </button>
                            </div>
                        </div>

                        {/* Expanded Section */}
                        {isExpanded && (
                            <div className="space-y-5 animate-fade-in">
                                {/* Divider */}
                                <div className="border-t border-slate-700" />

                                {/* Star Rating */}
                                <div>
                                    <p className="text-gray-300 text-sm mb-2">Rate this topic</p>
                                    <div className="flex gap-1 justify-center">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <button
                                                key={star}
                                                onClick={() => handleStarClick(star)}
                                                onMouseEnter={() => setHoveredStar(star)}
                                                onMouseLeave={() => setHoveredStar(null)}
                                                className="p-1 transition-transform hover:scale-110"
                                            >
                                                <svg
                                                    className={`w-8 h-8 transition-colors ${
                                                        star <= (hoveredStar || starRating || 0)
                                                            ? 'text-yellow-400 fill-yellow-400'
                                                            : 'text-slate-600'
                                                    }`}
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                </svg>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* What did you like most? */}
                                <div>
                                    <label className="text-gray-300 text-sm mb-1.5 block">
                                        What did you like most?
                                    </label>
                                    <textarea
                                        value={likedMost}
                                        onChange={(e) => handleTextChange(setLikedMost, e.target.value)}
                                        placeholder="Clear explanations, good examples..."
                                        className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                                        rows={2}
                                        maxLength={1000}
                                    />
                                </div>

                                {/* What could be improved? */}
                                <div>
                                    <label className="text-gray-300 text-sm mb-1.5 block">
                                        What could be improved?
                                    </label>
                                    <textarea
                                        value={couldImprove}
                                        onChange={(e) => handleTextChange(setCouldImprove, e.target.value)}
                                        placeholder="More examples, slower pace..."
                                        className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                                        rows={2}
                                        maxLength={1000}
                                    />
                                </div>

                                {/* What would you like to see more of? */}
                                <div>
                                    <label className="text-gray-300 text-sm mb-1.5 block">
                                        What would you like to see more of?
                                    </label>
                                    <textarea
                                        value={wantMoreOf}
                                        onChange={(e) => handleTextChange(setWantMoreOf, e.target.value)}
                                        placeholder="Hands-on exercises, real-world examples..."
                                        className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                                        rows={2}
                                        maxLength={1000}
                                    />
                                </div>

                                {/* Additional comments */}
                                <div>
                                    <label className="text-gray-300 text-sm mb-1.5 block">
                                        Any other feedback? <span className="text-gray-500">(optional)</span>
                                    </label>
                                    <textarea
                                        value={additionalComments}
                                        onChange={(e) => handleTextChange(setAdditionalComments, e.target.value)}
                                        placeholder="Share your thoughts..."
                                        className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                                        rows={2}
                                        maxLength={2000}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-5 py-4 bg-slate-900/50 border-t border-slate-700 flex items-center justify-between">
                        {/* Save Status */}
                        <div className="flex items-center gap-2 text-sm">
                            {saveStatus === 'saving' && (
                                <>
                                    <div className="w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                                    <span className="text-gray-400">Saving...</span>
                                </>
                            )}
                            {saveStatus === 'saved' && (
                                <>
                                    <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-green-400">Saved</span>
                                </>
                            )}
                            {saveStatus === 'error' && (
                                <>
                                    <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-red-400">Error saving</span>
                                </>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                            <button
                                onClick={handleClose}
                                className="px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm"
                            >
                                Skip
                            </button>
                            {isExpanded && (
                                <button
                                    onClick={handleSubmit}
                                    disabled={!sentiment || isSaving}
                                    className="px-5 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-2"
                                >
                                    {isSaving ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            Submit
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                            </svg>
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom Animations */}
            <style>{`
                @keyframes slide-in-right {
                    from {
                        transform: translateX(100%) translateY(-50%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0) translateY(-50%);
                        opacity: 1;
                    }
                }
                .animate-slide-in-right {
                    animation: slide-in-right 0.3s ease-out forwards;
                }
                @keyframes fade-in {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fade-in {
                    animation: fade-in 0.3s ease-out forwards;
                }
            `}</style>
        </>
    );
};

export default TopicReviewModal;
